import { NOTIFICATIONS_PAGE_SIZE } from '@alphadesk/config.ts';
import {
  API_PATHS,
  inboxPageSchema,
  type WebAction,
  type WireKindTally,
  type WireNotification,
} from '@alphadesk/web/contract.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { z } from 'zod/mini';
import { useToast } from '../components/Toast.tsx';
import { type InboxLens, isLensed, kindsOf, matchesLens } from './notes.ts';
import { readJson, send } from './reads.ts';
import { isScoped, noteInScope, type Scope } from './scope.ts';

export type InboxPage = z.infer<typeof inboxPageSchema>;

interface InboxCut {
  lens: InboxLens;
  scope: Scope;
}

const isCut = (cut: InboxCut): boolean => isLensed(cut.lens) || isScoped(cut.scope);

// Must match the server's `where` for the same cut.
const matchesCut = (row: WireNotification, cut: InboxCut): boolean =>
  matchesLens(row, cut.lens) && noteInScope(cut.scope, row);

function withinOf(cut: InboxCut): MarkWithin | null {
  const kinds = kindsOf(cut.lens);
  const { analystId, bookId } = cut.scope;
  if (kinds === undefined && analystId === null) return null;
  return { kinds, scope: analystId === null ? null : { analystId, bookId } };
}

const inKinds = (cut: InboxCut) => (kind: string) => matchesLens({ kind, readAt: null }, cut.lens);

// Filtered server-side: a filter over loaded rows would only reach as far back as they do.
async function fetchInbox(cut: InboxCut, before?: number | null): Promise<InboxPage> {
  const query = new URLSearchParams();
  if (before) query.set('before', String(before));
  const kinds = kindsOf(cut.lens);
  if (kinds !== undefined) query.set('kinds', kinds.join(','));
  if (cut.lens.unreadOnly) query.set('unread', '1');
  if (cut.scope.analystId !== null) query.set('analyst', String(cut.scope.analystId));
  if (cut.scope.bookId !== null) query.set('book', String(cut.scope.bookId));
  const suffix = query.size === 0 ? '' : `?${query}`;
  return readJson(`${API_PATHS.notifications}${suffix}`, inboxPageSchema);
}

const spent = (page: readonly WireNotification[]): boolean => page.length < NOTIFICATIONS_PAGE_SIZE;

interface Fetched {
  cut: InboxCut;
  rows: WireNotification[];
  tally: InboxPage['tally'] | null;
  exhausted: boolean;
}

/**
 * Uncut, the list is the socket's. Under a cut it is fetched server-side with live socket rows
 * merged in. `cut` must be memoised: a fetch belongs to the cut it was asked for.
 */
export function useInbox(
  notifications: readonly WireNotification[],
  receipts: ReadonlyMap<number, string>,
  cut: InboxCut,
  extend: (older: WireNotification[]) => void,
) {
  const cutting = isCut(cut);
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isCut(cut)) return;
    let live = true;
    void fetchInbox(cut)
      .then(
        (page): Fetched => ({
          cut,
          rows: page.notifications,
          tally: page.tally,
          exhausted: spent(page.notifications),
        }),
        (): Fetched => ({ cut, rows: [], tally: null, exhausted: true }),
      )
      .then((next) => {
        if (live) setFetched(next);
      });
    return () => {
      live = false;
    };
  }, [cut]);

  const current = fetched?.cut === cut ? fetched : null;

  // The fetch's `about` is the book now; the socket's copy adds only a read from another tab.
  const rows = useMemo(() => {
    if (!cutting) return notifications;
    const readAt = new Map(notifications.map((row) => [row.id, row.readAt]));
    const held = new Set(current?.rows.map((row) => row.id));
    return [
      ...notifications.filter((row) => !held.has(row.id)),
      ...(current?.rows ?? []).map((row) => ({
        ...row,
        readAt: row.readAt ?? readAt.get(row.id) ?? receipts.get(row.id) ?? null,
      })),
    ]
      .filter((row) => matchesCut(row, cut))
      .sort((a, b) => b.id - a.id);
  }, [cutting, current, notifications, receipts, cut]);

  // Under a cut, the unread are the cut's, so the lead counts what Mark all read reaches.
  const unread = useMemo(() => {
    if (!cutting || !current?.tally) return null;
    const counted = inKinds(cut);
    return current.tally.reduce((n, entry) => (counted(entry.kind) ? n + entry.unread : n), 0);
  }, [cutting, current, cut]);

  const noteRead = useCallback(
    (ids: readonly number[] | null) => {
      const at = new Date().toISOString();
      const counted = inKinds(cut);
      setFetched((was) => {
        if (!was) return was;
        const marking = (row: WireNotification) =>
          row.readAt === null && (ids === null || ids.includes(row.id));
        const marked = was.rows.filter(marking).map((row) => row.kind);
        const left = (entry: WireKindTally) =>
          ids !== null
            ? entry.unread - marked.filter((kind) => kind === entry.kind).length
            : counted(entry.kind)
              ? 0
              : entry.unread;
        return {
          ...was,
          rows: was.rows.map((row) => (marking(row) ? { ...row, readAt: at } : row)),
          tally: was.tally?.map((entry) => ({ ...entry, unread: left(entry) })) ?? null,
        };
      });
    },
    [cut],
  );

  const more = async () => {
    const last = rows.at(-1);
    if (!last) return;
    setLoading(true);
    try {
      const page = await fetchInbox(cut, last.id);
      if (!cutting) {
        if (spent(page.notifications)) setExhausted(true);
        extend(page.notifications);
      } else {
        setFetched((was) =>
          was?.cut !== cut
            ? was
            : {
                ...was,
                rows: [...was.rows, ...page.notifications],
                exhausted: spent(page.notifications),
              },
        );
      }
    } catch (error) {
      toast.refused([error instanceof Error ? error.message : 'Could not load more.']);
    } finally {
      setLoading(false);
    }
  };

  return {
    rows,
    tally: current?.tally ?? null,
    done: cutting ? (current?.exhausted ?? true) : exhausted,
    loading,
    more,
    noteRead,
    unread,
    markAllRead: () => markRead(null, withinOf(cut)),
  };
}

type MarkWithin = NonNullable<Extract<WebAction, { do: 'mark-read' }>['within']>;

// Null marks all read.
export async function markRead(
  ids: number[] | null,
  within: MarkWithin | null = null,
): Promise<void> {
  await send(API_PATHS.act, { method: 'POST', body: { do: 'mark-read', ids, within } });
}
